import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Handshake, Star, MapPin, Phone, Globe, MagnifyingGlass } from '@phosphor-icons/react';
import { DepartmentNav } from '../components/DepartmentNav';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

const SERVICE_TYPES = [
  'Lawn Care',
  'Lawn Leveling',
  'Landscaping',
  'Irrigation',
  'Pest Control',
  'Tree Service',
  'Hardscaping',
  'Fertilization & Weed Control',
  'Sod Installation',
  'Lawn Renovation',
  'Other'
];

export const PartnersPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav']);
  const [partners, setPartners] = useState([]);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  // Application form
  const [bizName, setBizName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [areaServed, setAreaServed] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');

  // Review form
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/partners`);
      setPartners(res.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/partners/apply`, {
        business_name: bizName,
        contact_name: contactName,
        email,
        phone,
        service_type: serviceType,
        area_served: areaServed,
        description,
        website: website || null
      });
      toast.success('Application submitted! We\'ll review it shortly.');
      setShowApplyDialog(false);
      setBizName(''); setContactName(''); setEmail(''); setPhone('');
      setServiceType(''); setAreaServed(''); setDescription(''); setWebsite('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!selectedPartner) return;
    try {
      await axios.post(
        `${API_URL}/api/partners/${selectedPartner.id}/review`,
        { rating, comment: comment || null },
        { withCredentials: true }
      );
      toast.success('Review submitted!');
      setShowReviewDialog(false);
      setRating(5);
      setComment('');
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const renderStars = (avg, count) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          weight={i <= Math.round(avg) ? 'fill' : 'regular'}
          size={16}
          className={i <= Math.round(avg) ? 'text-amber-500' : 'text-muted-foreground/30'}
        />
      );
    }
    return (
      <div className="flex items-center gap-1">
        {stars}
        <span className="text-sm text-muted-foreground ml-1">
          {avg > 0 ? `${avg}` : 'No reviews'} {count > 0 && `(${count})`}
        </span>
      </div>
    );
  };

  const filteredPartners = partners.filter(p => {
    const matchesSearch = p.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.area_served.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || p.service_type === filterType;
    return matchesSearch && matchesType;
  });

  const uniqueServiceTypes = [...new Set(partners.map(p => p.service_type))];

  return (
    <div data-testid="partners-page" className="min-h-screen bg-background">
      {user && <DepartmentNav />}

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 data-testid="partners-page-title" className="text-3xl font-bold flex items-center gap-2">
              <Handshake weight="duotone" size={32} className="text-primary" />
              Service Partners Directory
            </h2>
            <p className="text-muted-foreground">Vetted local pros trusted by the TurfPulse community</p>
          </div>
          <Button
            data-testid="become-partner-btn"
            onClick={() => setShowApplyDialog(true)}
            size="lg"
            className="shrink-0"
          >
            <Handshake weight="bold" size={20} className="mr-2" />
            Become a Partner
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="partners-search"
              placeholder="Search by name, area, or service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="partners-filter" className="w-full md:w-64">
              <SelectValue placeholder="Filter by service type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Service Types</SelectItem>
              {uniqueServiceTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Pro Pricing Banner */}
        <Card className="border-primary/30 bg-primary/5 dark:bg-primary/10">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="bg-primary text-primary-foreground rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
                Pro Pricing
              </div>
              <p className="text-sm">
                <strong>Round Rock, TX area?</strong> Get access to pro-grade products at wholesale pricing through our Horizon Distributors account. Just mention <strong>"Hi Tex Lawn Pro"</strong> at checkout.
                {!user && ' Sign up to learn more.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Partners Grid */}
        {filteredPartners.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPartners.map((partner) => (
              <Card key={partner.id} data-testid={`partner-card-${partner.id}`} className="flex flex-col">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{partner.business_name}</CardTitle>
                      <CardDescription>{partner.contact_name}</CardDescription>
                    </div>
                    <span className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground whitespace-nowrap">
                      {partner.service_type}
                    </span>
                  </div>
                  {renderStars(partner.average_rating, partner.rating_count)}
                </CardHeader>
                <CardContent className="flex-1 flex flex-col justify-between">
                  <p className="text-sm text-muted-foreground mb-4">{partner.description}</p>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-muted-foreground shrink-0" />
                      <span>{partner.area_served}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-muted-foreground shrink-0" />
                      <a href={`tel:${partner.phone}`} className="text-primary hover:underline">{partner.phone}</a>
                    </div>
                    {partner.website && (
                      <div className="flex items-center gap-2">
                        <Globe size={14} className="text-muted-foreground shrink-0" />
                        <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm truncate">
                          {partner.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}
                  </div>

                  {user && (
                    <Button
                      data-testid={`review-partner-${partner.id}`}
                      variant="outline"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={() => { setSelectedPartner(partner); setShowReviewDialog(true); }}
                    >
                      <Star weight="bold" size={14} className="mr-2" />
                      Leave a Review
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Handshake weight="duotone" size={64} className="mx-auto text-muted-foreground/30" />
            <p className="text-muted-foreground mt-4">
              {partners.length === 0 ? 'No partners listed yet. Be the first!' : 'No partners match your search.'}
            </p>
            <Button onClick={() => setShowApplyDialog(true)} className="mt-4">
              Become a Partner
            </Button>
          </div>
        )}
      </div>

      {/* Application Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Become a TurfPulse Partner</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleApply} className="space-y-4">
            <div>
              <Label>Business Name *</Label>
              <Input data-testid="apply-biz-name" value={bizName} onChange={(e) => setBizName(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Name *</Label>
                <Input data-testid="apply-contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input data-testid="apply-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone *</Label>
                <Input data-testid="apply-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required />
              </div>
              <div>
                <Label>Service Type *</Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger data-testid="apply-service-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Area Served *</Label>
              <Input data-testid="apply-area" value={areaServed} onChange={(e) => setAreaServed(e.target.value)} placeholder="e.g., Round Rock, TX and surrounding areas" required />
            </div>
            <div>
              <Label>Description *</Label>
              <Input data-testid="apply-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell homeowners about your services..." required />
            </div>
            <div>
              <Label>Website (optional)</Label>
              <Input data-testid="apply-website" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" />
            </div>
            <Button data-testid="apply-submit" type="submit" className="w-full">
              Submit Application
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Applications are reviewed by the TurfPulse team before being published.
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review {selectedPartner?.business_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleReview} className="space-y-4">
            <div>
              <Label>Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setRating(val)}
                    className="focus:outline-none"
                  >
                    <Star
                      weight={val <= rating ? 'fill' : 'regular'}
                      size={28}
                      className={val <= rating ? 'text-amber-500' : 'text-muted-foreground/30'}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Comment (optional)</Label>
              <Input
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience..."
              />
            </div>
            <Button data-testid="review-submit" type="submit" className="w-full">
              Submit Review
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
